// src/Login.js
import React, { useState } from 'react';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/system';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import BeatLoader from 'react-spinners/BeatLoader';
import HiraganaLoader from '../../components/HiraganaLoader';
import Alert from '@mui/material/Alert';
import Mina from '../../assets/images/logo.png';
import '../../styles/Login.css';
import mixpanel from '../../utils/mixpanel'  // Import the initialized Mixpanel instance
import { API_URL } from '../../app/constants'

const MainContainer = styled(Box)({
  minHeight: '100vh',
  backgroundColor: '#FFFFFF',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontFamily: 'Manrope, sans-serif',
  padding: '20px',
  '@media (min-width: 769px)': {
    alignItems: 'flex-start',
    paddingTop: '120px',
  },
  '@media (max-width: 768px)': {
    alignItems: 'flex-start',
    paddingTop: '120px',
    padding: '120px 20px 40px',
  },
});

const StyledContainer = styled(Container)({
  maxWidth: '700px',
  width: '700px',
  fontFamily: 'Manrope, sans-serif',
});

const StyledTextField = styled(TextField)({
  width: '100%',
  marginBottom: '16px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    backgroundColor: '#FFFFFF',
    transition: 'all 0.2s',
    '& fieldset': {
      border: '1px solid #E5E7EB',
      borderRadius: '4px',
    },
    '&:hover': {
      backgroundColor: '#FFFFFF',
      '& fieldset': {
        borderColor: '#D1D5DB',
      },
    },
    '&.Mui-focused': {
      backgroundColor: '#FFFFFF',
      boxShadow: '0 0 0 2px rgba(147, 51, 234, 0.1)',
      '& fieldset': {
        borderColor: '#9333EA',
        borderWidth: '1px',
      },
    },
  },
  '& .MuiInputBase-input': {
    padding: '12px 14px',
    fontSize: '15px',
    height: '16px',
  },
  '& .MuiInputLabel-root': {
    fontSize: '14px',
  },
  '& .MuiInputAdornment-root .MuiIconButton-root': {
    color: '#6B7280',
  },
});

const FieldTitle = styled(Typography)({
  textAlign: 'left',
  marginBottom: '4px',
  fontWeight: '500',
  fontSize: '14px',
  color: '#374151',
  fontFamily: 'Manrope, sans-serif',
});

const StyledButton = styled(Button)({
  width: '100%',
  marginTop: '1rem',
  marginBottom: '1rem',
  backgroundColor: '#9333EA',
  color: 'white',
  borderRadius: '4px !important',
  border: '1px solid #9333EA',
  padding: '12px 14px',
  fontWeight: 600,
  fontSize: '15px',
  textTransform: 'none',
  letterSpacing: 0,
  fontFamily: 'Manrope, sans-serif',
  minHeight: '48px',
  boxShadow: 'none !important',
  '&.MuiButton-root': {
    borderRadius: '4px !important',
  },
  '&:hover': {
    backgroundColor: '#7928CA',
    border: '1px solid #7928CA',
    boxShadow: 'none !important',
    borderRadius: '4px !important',
  },
  '&.Mui-disabled': {
    backgroundColor: '#E9D5FF',
    color: '#fff',
    border: '1px solid #E9D5FF',
    borderRadius: '4px !important',
  },
});

const StyledLink = styled(Link)({
  textDecoration: 'none',
  fontWeight: '600',
  color: '#9333EA',
  fontSize: '14px',
  fontFamily: 'Manrope, sans-serif',
  transition: 'all 0.2s',
  '&:hover': {
    color: '#7928CA',
    textDecoration: 'none',
  },
});

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const email = data.get('email');
    const password = data.get('password');

    const startTime = Date.now();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.message || `Failed to log in: ${response.status} ${response.statusText}`);
      }

      const accessToken = responseData.user?.access;
      const userId = responseData.user?.user_id;
      const userStatus = responseData.status;
      
      if (accessToken) {
        localStorage.setItem('jwt', accessToken);

        // Check email verification status after login
        try {
          const userResponse = await fetch(`${API_URL}/api/v1/auth/me`, {
            headers: { 'Authorization': `Bearer ${accessToken}` },
            method: 'GET',
          });
          
          if (userResponse.ok) {
            const userData = await userResponse.json();
            
            // If email is not verified, redirect to email verification
            if (!userData.email_verified) {
              // Ensure minimum loading time before redirect
              const elapsedTime = Date.now() - startTime;
              const minLoadingTime = 2000; // 2 seconds
              const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

              setTimeout(() => {
                window.location.href = '/email-verification';
              }, remainingTime);
              return;
            }
          }
        } catch (error) {
          console.error('Error checking email verification:', error);
        }

        // Mixpanel identify and set user properties
        if (userId) {
          mixpanel.identify(userId);
          
          // Track the login event
          mixpanel.track('Login', {
            email: email,
            status: userStatus
          });
        }

        // Ensure minimum loading time before redirect to dashboard
        const elapsedTime = Date.now() - startTime;
        const minLoadingTime = 2000; // 2 seconds
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

        setTimeout(() => {
          window.location.href = '/';
        }, remainingTime);
      } else {
        throw new Error('Access token is missing in the response');
      }
    } catch (error) {
      // Even on error, ensure minimum loading time
      const elapsedTime = Date.now() - startTime;
      const minLoadingTime = 2000;
      const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

      setTimeout(() => {
        setLoading(false);
        setErrorMessage(error.message);
      }, remainingTime);
    }
  };

  if (loading) return <HiraganaLoader />;

  return (
    <>
      <CssBaseline />
      <Box className="logo-container-box">
        <img src={Mina} alt="Mina Logo" className="mina-logo" />
      </Box>
      <MainContainer>
        <StyledContainer component="main">
          <Typography 
            component="h1" 
            variant="h4" 
            sx={{ 
              textAlign: 'center',
              fontWeight: 700, 
              fontSize: '1.8rem', 
              color: '#111827',
              marginBottom: '32px',
              fontFamily: 'Manrope, sans-serif'
            }}
          >
            Welcome back!
          </Typography>
          <Box sx={{ textAlign: 'center', marginBottom: '32px' }}>
            <Typography 
              sx={{ 
                color: '#64748b',
                fontSize: '1rem',
                fontFamily: 'Manrope, sans-serif'
              }}
            >
              Please enter your credentials to continue your learning journey.
            </Typography>
          </Box>
          {errorMessage && (
            <Alert 
              severity="error" 
              sx={{ 
                width: '100%', 
                marginBottom: 3,
                borderRadius: '4px',
                '& .MuiAlert-icon': {
                  color: '#F87171'
                },
                '& .MuiAlert-message': {
                  fontFamily: 'Manrope, sans-serif',
                  fontSize: '14px'
                }
              }}
            >
              Login failed, wrong credentials
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
            <FieldTitle>Email Address</FieldTitle>
            <StyledTextField
              required
              fullWidth
              id="email"
              name="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoFocus
              placeholder="e.g. john@example.com"
            />
            <FieldTitle>Password</FieldTitle>
            <StyledTextField
              required
              fullWidth
              name="password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              inputMode="text"
              autoComplete="current-password"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
              placeholder="e.g. MyPassword123!"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <StyledButton
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              disableElevation
            >
              {loading ? <BeatLoader color="#fff" size={10} /> : 'Log in'}
            </StyledButton>

            <Box sx={{ textAlign: 'center', marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Box>
                <StyledLink href="/register">
                  Don't have an account?
                </StyledLink>
              </Box>
              <Box>
                <StyledLink href="/forgotpassword">
                  Forgot password?
                </StyledLink>
              </Box>
            </Box>
          </Box>
        </StyledContainer>
      </MainContainer>
    </>
  );
}
